import {
	Button,
	Input,
	InputNumber,
	Checkbox,
	Select,
	Slider,
	Radio,
} from 'antd';
import { FilterFilled } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { type LocationLabelFormatter, locationSearchText } from '../../utils/locationLabel';

// BUNDLE ID FILTER
export const BundleIdFilterDropdown = ({ queryParams, navigate }: any) => {
	const applyFilter = (
		value: string | null,
		confirm: (closeDropdown?: boolean | undefined) => void
	) => {
		if (value) {
			queryParams.set('bundle_friendly_id', value);
		} else {
			queryParams.delete('bundle_friendly_id');
		}
		queryParams.set('page', '1');
		navigate({ search: queryParams.toString() });

		confirm();
	};

	return {
		filterDropdown: ({ clearFilters, confirm }: any) => {
			const [tempValue, setTempValue] = useState(
				queryParams.get('bundle_friendly_id') || ''
			);

			useEffect(() => {
				setTempValue(queryParams.get('bundle_friendly_id') || '');
			}, [queryParams.get('bundle_friendly_id')]);

			const isFilterActive = !!queryParams.get('bundle_friendly_id');

			const handleOk = () => {
				applyFilter(tempValue || null, confirm);
			};

			const handleReset = () => {
				setTempValue('');
				applyFilter(null, confirm);
				clearFilters?.();
			};

			return (
				<div style={{ padding: 8 }}>
					<Input
						placeholder="Search Bundle | Length ID"
						value={tempValue}
						onChange={(e) => setTempValue(e.target.value)}
						onPressEnter={handleOk}
						style={{ marginBottom: 8, display: 'block' }}
					/>
					<div
						style={{
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
							marginTop: 8,
						}}
					>
						<Button
							onClick={handleReset}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
							}}
							size="small"
							type="text"
							disabled={!isFilterActive}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleOk}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: (filtered: boolean) => (
			<FilterFilled style={{ color: filtered ? '#1890ff' : undefined }} />
		),
		filteredValue: queryParams.get('bundle_friendly_id') ? ['filtered'] : null,
	};
};

// TYPE FILTER
export const TypeFilterDropdown = (
	{ queryParams, navigate }: any,
	types: any[],
	categories: any[]
) => {
	const initialSelectedTypes = queryParams.get('types')?.split(',') || [];
	const initialSelectedCategory = queryParams.get('category') || '';

	const isFilterActive =
		initialSelectedTypes.length > 0 || initialSelectedCategory !== '';

	const [tempSelectedTypes, setTempSelectedTypes] =
		useState<string[]>(initialSelectedTypes);
	const [tempSelectedCategory, setTempSelectedCategory] = useState<string>(
		initialSelectedCategory
	);

	useEffect(() => {
		setTempSelectedTypes(initialSelectedTypes);
		setTempSelectedCategory(initialSelectedCategory);
	}, [queryParams.toString()]);

	const applyFilter = (
		categoryValue: string | null,
		typesValue: string | null
	) => {
		if (categoryValue) {
			queryParams.set('category', categoryValue);
		} else {
			queryParams.delete('category');
		}
		if (typesValue) {
			queryParams.set('types', typesValue);
		} else {
			queryParams.delete('types');
		}
		queryParams.set('page', '1');
		navigate({ search: queryParams.toString() });
	};

	const filteredTypes = tempSelectedCategory
		? types.filter(
				(type) => type.material_category?.id === tempSelectedCategory
		  )
		: types;

	return {
		filterDropdown: ({ clearFilters, confirm }: any) => {
			const handleCategoryChange = (value: string | null) => {
				setTempSelectedCategory(value || '');
				setTempSelectedTypes([]);
				const categoryValue = value || null;
				applyFilter(categoryValue, null);
			};

			const handleTypeCheckboxChange = (values: string[]) => {
				setTempSelectedTypes(values);
			};

			const handleOk = () => {
				const typesValue =
					tempSelectedTypes.length > 0 ? tempSelectedTypes.join(',') : null;
				const categoryValue = tempSelectedCategory || null;

				applyFilter(categoryValue, typesValue);

				confirm();
			};

			const handleReset = () => {
				setTempSelectedTypes([]);
				setTempSelectedCategory('');
				applyFilter(null, null);
				clearFilters?.();
				confirm();
			};

			return (
				<div style={{ padding: 8, minWidth: 280, width: 280 }}>
					<Select
						placeholder="Select Category"
						allowClear
						value={tempSelectedCategory || undefined}
						onChange={handleCategoryChange}
						style={{ width: '100%', marginBottom: 8 }}
					>
						{(categories || []).map((cat) => (
							<Select.Option key={cat.id} value={cat.id}>
								{cat.display_name}
							</Select.Option>
						))}
					</Select>

					<div
						style={{
							border: '1px solid #d9d9d9',
							borderRadius: 4,
							maxHeight: 160,
							overflowY: 'auto',
							padding: '4px 8px',
							marginBottom: 8,
						}}
					>
						<Checkbox.Group
							value={tempSelectedTypes}
							onChange={(values) =>
								handleTypeCheckboxChange(values as string[])
							}
							style={{
								width: '100%',
								display: 'flex',
								flexDirection: 'column',
							}}
						>
							{filteredTypes.map((type: any) => (
								<Checkbox
									key={type.id}
									value={type.id}
									style={{ margin: '4px 0', marginLeft: 0 }}
								>
									{type.display_name}
								</Checkbox>
							))}
						</Checkbox.Group>
					</div>

					<div
						style={{
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
							marginTop: 8,
						}}
					>
						<Button
							onClick={handleReset}
							style={{
								color:
									tempSelectedTypes.length > 0 || !!tempSelectedCategory
										? '#1677ff'
										: 'rgba(0, 0, 0, 0.25)',
							}}
							size="small"
							type="text"
							disabled={
								!(tempSelectedTypes.length > 0 || !!tempSelectedCategory)
							}
						>
							Reset
						</Button>

						<Button
							type="primary"
							onClick={handleOk}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: (filtered: boolean) => (
			<FilterFilled
				style={{ color: filtered || isFilterActive ? '#1890ff' : undefined }}
			/>
		),
		filteredValue: isFilterActive ? ['filtered'] : null,
	};
};

// SHELVES FILTER
export const ShelfFilterDropdown = (
	{ queryParams, navigate }: any,
	locations: any[],
	onSearchLocation?: (value: string) => void,
	loading?: boolean,
	formatLocation: LocationLabelFormatter = (text) => text
) => {
	// When an onSearchLocation handler is provided the dropdown searches the
	// backend (the same source as the sidebar Location filter), so newly created
	// locations outside the initial page are reachable. Otherwise it falls back
	// to the legacy client-side filter over the provided locations. See PTB-5925.
	const serverSide = typeof onSearchLocation === 'function';
	const applyFilter = (
		value: string | null,
		confirm: (closeDropdown?: boolean | undefined) => void
	) => {
		if (value) {
			queryParams.set('location_id', value);
		} else {
			queryParams.delete('location_id');
		}
		queryParams.set('page', '1');
		navigate({ search: queryParams.toString() });

		confirm();
	};

	return {
		filterDropdown: ({ clearFilters, confirm }: any) => {
			const [tempValue, setTempValue] = useState<string[]>(
				queryParams.get('location_id')?.split(',').filter(Boolean) || []
			);

			const [searchValue, setSearchValue] = useState('');

			useEffect(() => {
				setTempValue(
					queryParams.get('location_id')?.split(',').filter(Boolean) || []
				);
			}, [queryParams.toString()]);

			const LocationsGroupedByRack = locations?.reduce(
				(acc: { [key: string]: any[] }, location: any) => {
					const rackName = location.shelf.rack.rack_name || 'Unknown Rack';
					if (!acc[rackName]) {
						acc[rackName] = [];
					}
					acc[rackName].push(location);
					return acc;
				},
				{}
			);

			const filteredGroupedLocations: { [key: string]: any[] } = {};
			if (LocationsGroupedByRack) {
				Object.entries(LocationsGroupedByRack).forEach(
					([rackName, rackShelves]) => {
						// In server-side mode the backend already filtered by the
						// search term, so show everything it returned (mirrors the
						// sidebar's filterOption={false}). Otherwise filter locally.
						const matchesRack = rackName
							.toLowerCase()
							.includes(searchValue.toLowerCase());
						const filteredLocations = serverSide
							? rackShelves
							: rackShelves.filter(
									(location: any) =>
										matchesRack ||
										location.code
											.toLowerCase()
											.includes(searchValue.toLowerCase()) ||
										locationSearchText(location.display_name, formatLocation)
											.toLowerCase()
											.includes(searchValue.toLowerCase()) ||
										location.shelf.rack.rack_name
											?.toLowerCase()
											.includes(searchValue.toLowerCase())
							  );

						if (filteredLocations.length > 0) {
							filteredGroupedLocations[rackName] = filteredLocations;
						}
					}
				);
			}

			const isFilterActive = tempValue.length > 0;

			const handleApply = () => {
				applyFilter(tempValue.length > 0 ? tempValue.join(',') : null, confirm);
			};

			const handleReset = () => {
				setTempValue([]);
				setSearchValue('');

				applyFilter(null, confirm);
				clearFilters?.();
			};

			return (
				<div style={{ minWidth: 250, padding: 8 }}>
					<Input
						placeholder="Search location..."
						value={searchValue}
						onChange={(e) => {
							const value = e.target.value;
							setSearchValue(value);
							// Trigger the shared server-side search (debounced
							// upstream) so locations beyond the initial page are
							// reachable, consistent with the sidebar filter.
							onSearchLocation?.(value);
						}}
						style={{ marginBottom: 8 }}
						allowClear
					/>

					<div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 8 }}>
						{loading ? (
							<div
								style={{
									textAlign: 'center',
									color: '#999',
									padding: '20px 0',
								}}
							>
								Searching...
							</div>
						) : Object.keys(filteredGroupedLocations).length > 0 ? (
							Object.entries(filteredGroupedLocations).map(
								([rackName, rackLocations]) => (
									<div key={rackName} style={{ marginBottom: 12 }}>
										<div style={{ fontWeight: 'bold', marginBottom: 4 }}>
											{rackName}
										</div>
										{rackLocations.map((location: any) => (
											<div
												key={location.id}
												style={{ marginBottom: 4, marginLeft: 8 }}
											>
												<Checkbox
													checked={tempValue.includes(location.id)}
													onChange={(e) => {
														if (e.target.checked) {
															setTempValue([...tempValue, location.id]);
														} else {
															setTempValue(
																tempValue.filter((id) => id !== location.id)
															);
														}
													}}
												>
													{formatLocation(location.display_name)}
												</Checkbox>
											</div>
										))}
									</div>
								)
							)
						) : (
							<div
								style={{
									textAlign: 'center',
									color: '#999',
									padding: '20px 0',
								}}
							>
								No shelves found
							</div>
						)}
					</div>

					<div
						style={{
							borderTop: '1px solid #f0f0f0',
							paddingTop: 8,
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
						}}
					>
						<Button
							onClick={handleReset}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
							}}
							size="small"
							type="text"
							disabled={!isFilterActive}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleApply}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: (filtered: boolean) => (
			<FilterFilled style={{ color: filtered ? '#1890ff' : undefined }} />
		),
		filteredValue: queryParams.get('location_id') ? ['filtered'] : null,
	};
};

// RANGE FILTER (Height, Width, etc.)
export const RangeFilterDropdown = (
	{ queryParams, navigate }: any,
	filterKey: string,
	label: string,
	dynamicRange?: { min: number; max: number }
) => {
	const applyFilter = (
		min: number | null,
		max: number | null,
		confirm: (closeDropdown?: boolean | undefined) => void
	) => {
		if (min !== null) {
			queryParams.set(`${filterKey}__gte`, String(min));
		} else {
			queryParams.delete(`${filterKey}__gte`);
		}
		if (max !== null) {
			queryParams.set(`${filterKey}__lte`, String(max));
		} else {
			queryParams.delete(`${filterKey}__lte`);
		}
		queryParams.set('page', '1');
		navigate({ search: queryParams.toString() });
		confirm();
	};

	const rangeMin = dynamicRange?.min ?? 0;
	const rangeMax = dynamicRange?.max ?? 999;
	const hasValidRange = dynamicRange && (rangeMin !== 0 || rangeMax !== 999);

	return {
		filterDropdown: ({ clearFilters, confirm }: any) => {
			const [minValue, setMinValue] = useState<number | null>(
				Number(queryParams.get(`${filterKey}__gte`)) || null
			);
			const [maxValue, setMaxValue] = useState<number | null>(
				Number(queryParams.get(`${filterKey}__lte`)) || null
			);

			useEffect(() => {
				const urlMin = queryParams.get(`${filterKey}__gte`);
				const urlMax = queryParams.get(`${filterKey}__lte`);
				setMinValue(urlMin ? Number(urlMin) : null);
				setMaxValue(urlMax ? Number(urlMax) : null);
			}, [
				queryParams.get(`${filterKey}__gte`),
				queryParams.get(`${filterKey}__lte`),
			]);

			const isFilterActive =
				!!queryParams.get(`${filterKey}__gte`) ||
				!!queryParams.get(`${filterKey}__lte`);

			return (
				<div style={{ padding: 8, width: 280 }}>
					<InputNumber
						placeholder={`Min ${label}`}
						value={minValue}
						onChange={(value) => setMinValue(value)}
						style={{ width: '100%', marginBottom: 8 }}
						step={0.25}
						min={hasValidRange ? rangeMin : undefined}
						max={hasValidRange ? rangeMax : undefined}
						suffix="in"
					/>
					<InputNumber
						placeholder={`Max ${label}`}
						value={maxValue}
						onChange={(value) => setMaxValue(value)}
						style={{ width: '100%', marginBottom: 8 }}
						step={0.25}
						min={hasValidRange ? rangeMin : undefined}
						max={hasValidRange ? rangeMax : undefined}
						suffix="in"
					/>
					{hasValidRange && (
						<>
							<Slider
								range
								min={rangeMin}
								max={rangeMax}
								step={0.25}
								value={[minValue ?? rangeMin, maxValue ?? rangeMax]}
								onChange={(value) => {
									setMinValue(value[0]);
									setMaxValue(value[1]);
								}}
								tooltip={{ formatter: (value) => `${value}"` }}
								style={{ marginBottom: 8 }}
							/>
							<div
								style={{
									fontSize: '12px',
									color: '#999',
									marginBottom: 8,
									textAlign: 'center',
								}}
							>
								Available: {rangeMin.toFixed(2)}" - {rangeMax.toFixed(2)}"
							</div>
						</>
					)}
					<div
						style={{
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
						}}
					>
						<Button
							onClick={() => {
								setMinValue(null);
								setMaxValue(null);
								applyFilter(null, null, confirm);
								clearFilters?.();
							}}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
							}}
							size="small"
							type="text"
							disabled={!isFilterActive}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={() => applyFilter(minValue, maxValue, confirm)}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: (filtered: boolean) => (
			<FilterFilled
				style={{
					color:
						queryParams.get(`${filterKey}__gte`) ||
						queryParams.get(`${filterKey}__lte`)
							? '#1890ff'
							: undefined,
				}}
			/>
		),
		filteredValue:
			queryParams.get(`${filterKey}__gte`) ||
			queryParams.get(`${filterKey}__lte`)
				? ['filtered']
				: null,
	};
};

// CERTIFICATE FILTER
export const CertificateFilterDropdown = ({ queryParams, navigate }: any) => {
	const applyFilter = (value: string | null) => {
		if (value) {
			queryParams.set('has_certificate', value);
		} else {
			queryParams.delete('has_certificate');
		}
		queryParams.set('page', '1');
		navigate({ search: queryParams.toString() });
	};

	const resetFilterAndNavigate = () => {
		queryParams.delete('has_certificate');
		queryParams.set('page', '1');
		navigate({ search: queryParams.toString() });
	};

	return {
		filterDropdown: ({ clearFilters, confirm }: any) => {
			const [tempValue, setTempValue] = useState<string | null>(
				queryParams.get('has_certificate') || null
			);

			useEffect(() => {
				setTempValue(queryParams.get('has_certificate') || null);
			}, [queryParams.get('has_certificate')]);

			const isFilterActive = !!queryParams.get('has_certificate');

			const handleOk = () => {
				applyFilter(tempValue);
				confirm();
			};

			const handleReset = () => {
				setTempValue(null);

				resetFilterAndNavigate();

				clearFilters?.();
				confirm();
			};

			return (
				<div style={{ padding: 8, width: 200 }}>
					<Radio.Group
						value={tempValue}
						onChange={(e) => setTempValue(e.target.value)}
						style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
					>
						<Radio value="True">Has Certificate</Radio>
						<Radio value="False">No Certificate</Radio>
					</Radio.Group>

					<div
						style={{
							borderTop: '1px solid #f0f0f0',
							paddingTop: 8,
							marginTop: 8,
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
						}}
					>
						<Button
							onClick={handleReset}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
							}}
							size="small"
							type="text"
							disabled={!isFilterActive}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleOk}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: (filtered: boolean) => (
			<FilterFilled style={{ color: filtered ? '#1890ff' : undefined }} />
		),
		filteredValue: queryParams.get('has_certificate') ? ['filtered'] : null,
	};
};

// Minimum Available Length
export const MinimumFilterDropdown = (
	{ queryParams, navigate }: any,
	filterKey: string,
	label: string,
	dynamicRange?: { min: number; max: number }
) => {
	const applyFilter = (value: number | null) => {
		if (value !== null) {
			queryParams.set(filterKey, String(value));
		} else {
			queryParams.delete(filterKey);
		}
		queryParams.set('page', '1');
		navigate({ search: queryParams.toString() });
	};

	return {
		filterDropdown: ({ clearFilters, confirm }: any) => {
			const [value, setValue] = useState<number | null>(
				Number(queryParams.get(filterKey)) || null
			);

			useEffect(() => {
				const urlValue = queryParams.get(filterKey);
				setValue(urlValue ? Number(urlValue) : null);
			}, [queryParams.get(filterKey)]);

			const isFilterActive = !!queryParams.get(filterKey);

			const handleOk = () => {
				applyFilter(value);
				confirm();
			};

			const handleReset = () => {
				setValue(null);
				applyFilter(null);
				clearFilters?.();
				confirm();
			};

			return (
				<div style={{ padding: 8, width: 200 }}>
					<InputNumber
						placeholder={`Minimum ${label}`}
						value={value}
						onChange={(newValue) => setValue(newValue)}
						style={{ width: '100%', marginBottom: 8 }}
						step={0.25}
						min={dynamicRange?.min ?? 0}
						max={dynamicRange?.max ?? undefined}
						suffix="in"
					/>
					<div
						style={{
							display: 'flex',
							gap: 8,
							justifyContent: 'space-between',
						}}
					>
						<Button
							onClick={handleReset}
							style={{
								color: isFilterActive ? '#1677ff' : 'rgba(0, 0, 0, 0.25)',
							}}
							size="small"
							type="text"
							disabled={!isFilterActive}
						>
							Reset
						</Button>
						<Button
							type="primary"
							onClick={handleOk}
							size="small"
							style={{ width: 90 }}
						>
							Ok
						</Button>
					</div>
				</div>
			);
		},
		filterIcon: (filtered: boolean) => (
			<FilterFilled
				style={{
					color: queryParams.get(filterKey) ? '#1890ff' : undefined,
				}}
			/>
		),
		filteredValue: queryParams.get(filterKey) ? ['filtered'] : null,
	};
};
